/**
 * 灵动在途 API 模块
 *
 * 前端业务接口统一使用 /webserver/waybill。
 * /open/api/waybill 仅供外部客户调用，不能被前端页面直接使用。
 */
import request, { isRequestSuccess } from '@/utils/request'
const embedClient = require('@/embed/embedClient')

const BASE = '/webserver/waybill'

async function getAllEmbedWaybills(params = {}) {
  const items = []
  let cursor = null
  for (let page = 0; page < 20; page++) {
    const response = await embedClient.embedRequest('/waybills', {
      method: 'get',
      params: { ...params, cursor, limit: 100 }
    })
    const data = response.data || {}
    ;(data.items || []).forEach(item => items.push(embedClient.toInternalWaybill(item)))
    cursor = data.nextCursor
    if (!cursor) break
  }
  return { success: true, code: 200, msg: '操作成功', data: items }
}

/** ==================== 运单 ==================== */
export function getWaybillList(params) {
  if (embedClient.isEmbedMode()) return getAllEmbedWaybills(params)
  return request({ url: `${BASE}/waybillList`, method: 'get', params })
}
export function queryWaybillList(params) {
  return request({ url: `${BASE}/listQuery`, method: 'get', params })
}
export function getWaybillFileRecords(params) {
  return request({ url: `${BASE}/fileRecords`, method: 'get', params })
}
export function downloadWaybillFileRecord(id) {
  return request({ url: `${BASE}/fileRecords/${encodeURIComponent(id)}/download`, method: 'get', responseType: 'blob' })
}
export function changeWaybillAddress(data) {
  return request({ url: `${BASE}/addressChange`, method: 'post', data })
}
export function getAccountShareUsers(params) {
  return request({ url: `${BASE}/accountShare/accounts`, method: 'get', params })
}
export function createAccountShares(data) {
  return request({ url: `${BASE}/accountShare`, method: 'post', data })
}
export function getAccountShares(params) {
  return request({ url: `${BASE}/accountShare`, method: 'get', params })
}
export function revokeAccountShare(id) {
  return request({ url: `${BASE}/accountShare/revoke`, method: 'post', data: { id } })
}
export function getReceivedWaybills(params) {
  return request({ url: `${BASE}/accountShare/received`, method: 'get', params })
}
export function getReceivedWaybillData(id, resource, params) {
  return request({ url: `${BASE}/accountShare/${encodeURIComponent(id)}/${resource}`, method: 'get', params })
}
export async function getMapOverview(params = {}) {
  const waybills = []
  const monitors = {}
  const cursors = new Set()
  const query = { ...params, statuses: Array.isArray(params.statuses) ? params.statuses.join(',') : params.statuses === undefined ? '1' : params.statuses, limit: 200 }
  let cursor = null
  let total = null
  do {
    const config = { method: 'get', params: { ...query, cursor } }
    const response = embedClient.isEmbedMode()
      ? await embedClient.embedRequest('/overview/page', config)
      : await request({ url: `${BASE}/mapOverview/page`, ...config })
    if (!isRequestSuccess(response)) throw new Error(response.msg || '全图概览查询失败')
    const data = response.data
    if (!data || !Array.isArray(data.items) || data.total == null || !Number.isInteger(Number(data.total)) || Number(data.total) < 0) throw new Error('全图分页响应不完整，请重试')
    if (data.limitExceeded || Number(data.total) > 5000) throw new Error('匹配运单超过5000条，请缩小筛选范围')
    if (total !== null && total !== Number(data.total)) throw new Error('查询期间数据已变化，请重新查询')
    total = Number(data.total)
    data.items.forEach(item => waybills.push(embedClient.toInternalWaybill(item)))
    if (waybills.length > 5000) throw new Error('匹配运单超过5000条，请缩小筛选范围')
    Object.keys(data.positions || {}).forEach(waybillNo => { monitors[waybillNo] = embedClient.toInternalPosition(data.positions[waybillNo]) })
    cursor = data.nextCursor || null
    if (cursor && (!data.items.length || waybills.length >= total)) throw new Error('全图分页结果未前进或数量异常，请重新查询')
    if (cursor && cursors.has(cursor)) throw new Error('全图分页游标未前进，请重新查询')
    if (cursor) cursors.add(cursor)
  } while (cursor)
  if (waybills.length !== total || new Set(waybills.map(item => item.waybillNo)).size !== waybills.length) throw new Error('查询结果不完整或数据已变化，请重新查询')
  return { success: true, code: 200, msg: '操作成功', data: { waybills, monitors } }
}
export async function getFullMapAlarmSummary(params = {}) {
  if (embedClient.isEmbedMode()) throw new Error('嵌入组件不支持账号全局告警汇总')
  return request({ url: `${BASE}/mapOverview/alarmSummary`, method: 'get', params: { alarmType: params.alarmType || 'ALL' } })
}
export async function searchAreaVehicles(data) {
  if (embedClient.isEmbedMode()) {
    const response = await embedClient.embedRequest('/vehicles/positions/search', {
      method: 'post', data: { ...data, limit: 100 }
    })
    return { ...response, data: ((response.data && response.data.items) || [])
      .map(embedClient.toInternalAreaVehicle) }
  }
  return request({ url: `${BASE}/vehicles/positions/search`, method: 'post', data })
}
export async function getWaybillDetail(params) {
  if (embedClient.isEmbedMode()) {
    const response = await embedClient.embedRequest(
      `/waybills/${encodeURIComponent(params.transportNo)}`, { method: 'get' })
    return { ...response, data: embedClient.toInternalDetail(response.data) }
  }
  return request({ url: `${BASE}/waybillDetail`, method: 'get', params })
}
export function getMonitorStatus(params) {
  if (embedClient.isEmbedMode()) {
    return Promise.resolve({ success: true, code: 200, msg: '操作成功', data: [] })
  }
  return request({ url: `${BASE}/monitorStatus`, method: 'get', params })
}
export async function getMonitorTrack(params) {
  if (embedClient.isEmbedMode()) {
    const afterTime = params.afterTime === undefined || params.afterTime === null
      ? undefined : new Date(Number(params.afterTime)).toISOString()
    const response = await embedClient.embedRequest(
      `/waybills/${encodeURIComponent(params.transportNo)}/tracks`, {
        method: 'get',
        params: {
          afterTime,
          afterBackfillId: params.afterBackfillId,
          limit: Math.min(Number(params.limit) || 100, 100)
        }
      })
    return { ...response, data: embedClient.toInternalTrackPage(response.data) }
  }
  return request({ url: `${BASE}/monitorTrack`, method: 'get', params })
}
export function refreshMonitorTrack(data) {
  if (embedClient.isEmbedMode()) return getMonitorTrack(data)
  return request({ url: `${BASE}/monitorTrackRefresh`, method: 'post', data })
}
export function createWaybillShare(data) {
  return request({ url: `${BASE}/share`, method: 'post', data })
}
export function revokeWaybillShare(data) {
  return request({ url: `${BASE}/share/revoke`, method: 'post', data })
}
export function getWaybillShareDetail(shareId, params = {}) {
  return request({ url: `${BASE}/share/detail`, method: 'get', params: { ...params, shareId } })
}
export function createWaybill(data) {
  return request({ url: `${BASE}/transportSave`, method: 'post', data })
}
export function updateWaybill(transportNo, data) {
  return request({ url: `${BASE}/transportUpdate`, method: 'put', params: { transportNo }, data })
}
export function deleteWaybill(transportNo) {
  return request({ url: `${BASE}/transportDelete/${encodeURIComponent(transportNo)}`, method: 'delete' })
}
export function cancelWaybill(data) {
  return request({ url: `${BASE}/cancelTransport`, method: 'post', data })
}
export function changeWaybillStatus(data) {
  if (embedClient.isEmbedMode()) {
    const action = Number(data.status) === 1 ? 'start' : 'stop'
    return embedClient.embedRequest(
      `/waybills/${encodeURIComponent(data.transportNo)}/actions/${action}`,
      { method: 'post' })
  }
  return request({ url: `${BASE}/transportStatusChg`, method: 'post', data })
}
export function importWaybills(file) {
  const formData = new FormData()
  formData.append('file', file)
  return request({ url: `${BASE}/listImport`, method: 'post', data: formData })
}
export function downloadWaybillImportTemplate() {
  return request({ url: `${BASE}/waybillImportTemplate`, method: 'get', responseType: 'blob' })
}
export function exportWaybillList(params) {
  return request({ url: `${BASE}/listExport`, method: 'get', params, responseType: 'blob' })
}
export async function getAlarmList(params) {
  if (embedClient.isEmbedMode()) {
    const response = await embedClient.embedRequest(
      `/waybills/${encodeURIComponent(params.transportNo)}/alarms`, {
        method: 'get', params: { limit: Math.min(Number(params.limit) || 50, 100) }
      })
    const items = ((response.data && response.data.items) || [])
      .map(embedClient.toInternalAlarm)
    return { ...response, data: { items, total: items.length } }
  }
  return request({ url: `${BASE}/alarmList`, method: 'get', params })
}
export function exportAlarms(params) {
  return request({ url: `${BASE}/alarmExport`, method: 'get', params, responseType: 'blob' })
}
export function handleAlarm(data) {
  return request({ url: `${BASE}/handleAlarm`, method: 'post', data })
}
export function getAlarmHistory(params) {
  return request({ url: `${BASE}/alarmHistory`, method: 'get', params })
}
export function getAlarmRule() {
  return request({ url: `${BASE}/alarmSetting`, method: 'get' })
}
export function saveAlarmRule(data) {
  return request({ url: `${BASE}/alarmSetting`, method: 'post', data })
}
export function getAlarmScopes(params) {
  return request({ url: `${BASE}/alarmSetting/scopes`, method: 'get', params })
}
export function getAlarmScope(params) {
  return request({ url: `${BASE}/alarmSetting/scope`, method: 'get', params })
}
export function saveAlarmScope(data) {
  return request({ url: `${BASE}/alarmSetting/scope`, method: 'post', data })
}
export function deleteAlarmScope(id) {
  return request({ url: `${BASE}/alarmSetting/scope/${encodeURIComponent(id)}`, method: 'delete' })
}
export function getAlarmScopeVehicles(params) {
  return request({ url: `${BASE}/alarmSetting/vehicles`, method: 'get', params })
}
export function getAlarmScopeWaybills(params) {
  return request({ url: `${BASE}/alarmSetting/waybills`, method: 'get', params })
}
export async function getEta(transportNo) {
  if (embedClient.isEmbedMode()) {
    return embedClient.embedRequest(
      `/waybills/${encodeURIComponent(transportNo)}/eta`, { method: 'get' })
  }
  return request({ url: `${BASE}/eta`, method: 'get', params: { transportNo } })
}
export function getFenceList(params) {
  return request({ url: `${BASE}/fenceList`, method: 'get', params })
}
export function saveFence(data, params) {
  return request({ url: `${BASE}/fenceSave`, method: 'post', data, params })
}
export function updateFence(fenceCode, data) {
  return request({ url: `${BASE}/fence/${encodeURIComponent(fenceCode)}`, method: 'put', data })
}
export function updateFenceStatus(fenceCode, status) {
  return request({ url: `${BASE}/fence/${encodeURIComponent(fenceCode)}/status`, method: 'put', data: { status } })
}
export function deleteFence(fenceCode) {
  return request({ url: `${BASE}/fence/${encodeURIComponent(fenceCode)}`, method: 'delete' })
}
export function getFenceReferences(fenceCode, params) {
  return request({ url: `${BASE}/fence/${encodeURIComponent(fenceCode)}/references`, method: 'get', params })
}
export function getDriverList() {
  return request({ url: `${BASE}/driverList`, method: 'get' })
}
export function getDriverPage(params) {
  return request({ url: `${BASE}/driverPage`, method: 'get', params })
}
export function exportDrivers(params) {
  return request({ url: `${BASE}/driverExport`, method: 'get', params, responseType: 'blob' })
}
export function updateDriverStatus(id, enabled) {
  return request({ url: `${BASE}/driver/${id}/status`, method: 'put', data: { enabled } })
}
export function saveDriver(data) {
  return request({ url: `${BASE}/driverSave`, method: 'post', data })
}
export function updateDriver(data) {
  return request({ url: `${BASE}/driverUpdate`, method: 'put', data })
}
export function deleteDriver(id) {
  return request({ url: `${BASE}/driverDelete/${id}`, method: 'delete' })
}

/** ==================== 车辆与分组 ==================== */
export function getVehicleGroups() {
  return request({ url: `${BASE}/vehicle/groups`, method: 'get' })
}
export function createVehicleGroup(data) {
  return request({ url: `${BASE}/vehicle/groups`, method: 'post', data })
}
export function updateVehicleGroup(id, data) {
  return request({ url: `${BASE}/vehicle/groups/${id}`, method: 'put', data })
}
export function deleteVehicleGroup(id) {
  return request({ url: `${BASE}/vehicle/groups/${id}`, method: 'delete' })
}
export function getVehicleList(params) {
  return request({ url: `${BASE}/vehicles`, method: 'get', params })
}
export function saveVehicle(data) {
  return request({ url: `${BASE}/vehicles`, method: 'post', data })
}
export function updateVehicle(id, data) {
  return request({ url: `${BASE}/vehicles/${id}`, method: 'put', data })
}
export function deleteVehicle(id) {
  return request({ url: `${BASE}/vehicles/${id}`, method: 'delete' })
}
export function updateVehicleStatus(id, enabled) {
  return request({ url: `${BASE}/vehicles/${id}/status`, method: 'put', data: { enabled } })
}
export function batchDeleteVehicles(ids) {
  return request({ url: `${BASE}/vehicles/batch-delete`, method: 'post', data: { ids } })
}
export function getVehicleTree(params) {
  return request({ url: `${BASE}/vehicle/tree`, method: 'get', params })
}
export function exportVehicles(params) {
  return request({ url: `${BASE}/vehicles/export`, method: 'get', params, responseType: 'blob' })
}
export async function getJourneyNodes(params) {
  if (embedClient.isEmbedMode()) {
    return embedClient.embedRequest(
      `/waybills/${encodeURIComponent(params.transportNo)}/journey-nodes`, {
        method: 'get', params: { ...params, transportNo: undefined, limit: Math.min(Number(params.limit) || 100, 100) }
      })
  }
  return request({ url: `${BASE}/journeyNodes`, method: 'get', params })
}

/** ==================== 地址簿 ==================== */
export function getAddressBookList(params) {
  return request({ url: `${BASE}/addressBook/list`, method: 'get', params })
}
export function getAddressBookDetail(id) {
  return request({ url: `${BASE}/addressBook/${id}`, method: 'get' })
}
export function createAddressBook(data) {
  return request({ url: `${BASE}/addressBook`, method: 'post', data })
}
export function updateAddressBook(id, data) {
  return request({ url: `${BASE}/addressBook/${id}`, method: 'put', data })
}
export function deleteAddressBook(id) {
  return request({ url: `${BASE}/addressBook/${id}`, method: 'delete' })
}
export function updateAddressBookStatus(id, enabled) {
  return request({ url: `${BASE}/addressBook/${id}/status`, method: 'put', data: { enabled } })
}

/** ==================== 路线管理 ==================== */
export function getRouteList(params) {
  return request({ url: `${BASE}/routeList`, method: 'get', params })
}
export function getRouteDetail(id) {
  return request({ url: `${BASE}/routeDetail`, method: 'get', params: { id } })
}
export function getAvailableRoutes() {
  return request({ url: `${BASE}/routeAvailable`, method: 'get' })
}
export function checkRoutePassage(data) {
  return request({ url: `${BASE}/routePassage/check`, method: 'post', data })
}
export function previewRouteCoordinates(data) {
  return request({ url: `${BASE}/routeCoordinates/preview`, method: 'post', data })
}
export function saveRouteCoordinates(data) {
  return request({ url: `${BASE}/routeCoordinates`, method: 'put', data })
}
export function saveRoute(data) {
  return request({ url: `${BASE}/routeSave`, method: 'post', data })
}
export function updateRoute(data) {
  return request({ url: `${BASE}/routeUpdate`, method: 'put', data })
}
export function updateRouteStatus(data) {
  return request({ url: `${BASE}/routeStatusUpdate`, method: 'put', data })
}
export function deleteRoute(id) {
  return request({ url: `${BASE}/routeDelete/${id}`, method: 'delete' })
}
export function getRouteReferences(id, params) {
  return request({ url: `${BASE}/route/${id}/references`, method: 'get', params })
}
export { isRequestSuccess }
